(() => {
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const inputSelector = '[data-fg-variant-preview][data-fg-preview-image-url]';
  const groupSelector = '[data-fg-variant-preview-group]';
  const previewCache = new Map();
  let activeInput = null;
  let previewRequest = 0;

  const getInput = (target) => {
    if (!(target instanceof Element)) return null;
    const directInput = target.closest(inputSelector);
    if (directInput) return directInput;

    const label = target.closest('label[for]');
    if (!label) return null;
    const input = document.getElementById(label.htmlFor);
    return input?.matches(inputSelector) ? input : null;
  };

  const getOverlay = (input) =>
    input.closest('product-info')?.querySelector('[data-fg-variant-hover-preview]') || null;

  const canWarmPreviews = () => {
    if (!hoverQuery.matches || navigator.connection?.saveData) return false;
    return !['slow-2g', '2g'].includes(navigator.connection?.effectiveType);
  };

  const loadPreview = (url, priority = 'low') => {
    const cached = previewCache.get(url);
    if (cached) {
      if (priority === 'high') cached.image.fetchPriority = 'high';
      return cached.promise;
    }

    const preloadImage = new Image();
    preloadImage.decoding = 'async';
    preloadImage.fetchPriority = priority;

    const promise = new Promise((resolve) => {
      preloadImage.addEventListener(
        'load',
        async () => {
          try {
            await preloadImage.decode();
          } catch {
          }
          resolve(preloadImage);
        },
        { once: true }
      );
      preloadImage.addEventListener(
        'error',
        () => {
          previewCache.delete(url);
          resolve(null);
        },
        { once: true }
      );
    });

    previewCache.set(url, { image: preloadImage, promise });
    preloadImage.src = url;
    return promise;
  };

  const warmPreviews = (root = document) => {
    if (!canWarmPreviews()) return;
    const urls = new Set(
      Array.from(root.querySelectorAll(inputSelector), (input) => input.dataset.fgPreviewImageUrl).filter(Boolean)
    );
    urls.forEach((url) => loadPreview(url));
  };

  const scheduleWarmPreviews = (root = document) => {
    if (!canWarmPreviews()) return;
    const run = () => warmPreviews(root);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 800 });
    } else {
      window.setTimeout(run, 200);
    }
  };

  const hidePreview = (input = activeInput) => {
    if (!input) return;
    previewRequest += 1;
    const overlay = getOverlay(input);
    overlay?.classList.remove('is-visible');
    activeInput = null;
  };

  const showPreview = async (input) => {
    if (!input || !hoverQuery.matches) return;
    const overlay = getOverlay(input);
    const image = overlay?.querySelector('img');
    const url = input.dataset.fgPreviewImageUrl;
    if (!overlay || !image || !url) return;

    const requestId = ++previewRequest;
    activeInput = input;
    overlay.classList.remove('is-visible');
    const loadedImage = await loadPreview(url, 'high');
    if (!loadedImage || requestId !== previewRequest || activeInput !== input) return;

    image.src = url;
    image.alt = input.dataset.fgPreviewImageAlt || '';
    overlay.classList.add('is-visible');
  };

  document.addEventListener('pointerover', (event) => {
    if (!hoverQuery.matches) return;
    const group = event.target instanceof Element ? event.target.closest(groupSelector) : null;
    if (group) warmPreviews(group);
    const input = getInput(event.target);
    if (input && input !== activeInput) showPreview(input);
  });

  document.addEventListener('pointerout', (event) => {
    if (!activeInput) return;
    const nextInput = getInput(event.relatedTarget);
    if (nextInput) {
      if (nextInput !== activeInput) showPreview(nextInput);
      return;
    }
    hidePreview();
  });

  document.addEventListener('change', (event) => {
    if (getInput(event.target)) hidePreview();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hidePreview();
  });

  if (document.readyState === 'complete') {
    scheduleWarmPreviews();
  } else {
    window.addEventListener('load', () => scheduleWarmPreviews(), { once: true });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => scheduleWarmPreviews());
  }
})();
